// Web Audio API utilities for generating sounds

// Pentatonic scale frequencies (C Major Pentatonic: C, D, E, G, A)
// Using 4th octave as base
const FREQUENCIES: Record<string, number> = {
  '1': 261.63, // C4 (Do)
  '2': 293.66, // D4 (Re)
  '3': 329.63, // E4 (Mi)
  '4': 349.23, // F4 (Fa - usually skipped in pentatonic)
  '5': 392.0, // G4 (Sol)
  '6': 440.0, // A4 (La)
  '7': 493.88, // B4 (Ti - usually skipped in pentatonic)
  "1'": 523.25, // C5 (High Do)
}

let audioContext: AudioContext | null = null

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)()
  }
  return audioContext
}

export const playNote = (
  pitch: string,
  duration: number = 0.5,
  type: OscillatorType = 'sine',
) => {
  try {
    const ctx = getAudioContext()
    const osc = ctx.createOscillator()
    const gainNode = ctx.createGain()

    osc.type = type
    osc.frequency.value = FREQUENCIES[pitch] || 440

    osc.connect(gainNode)
    gainNode.connect(ctx.destination)

    const now = ctx.currentTime

    // Envelope
    gainNode.gain.setValueAtTime(0, now)
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.05) // Attack
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration) // Decay

    osc.start(now)
    osc.stop(now + duration)
  } catch (e) {
    console.error('Audio playback failed', e)
  }
}

export const playDrum = (type: 'kick' | 'snare' | 'hihat' | 'woodblock') => {
  try {
    const ctx = getAudioContext()
    const now = ctx.currentTime

    if (type === 'kick') {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.frequency.setValueAtTime(150, now)
      osc.frequency.exponentialRampToValueAtTime(0.01, now + 0.5)

      gain.gain.setValueAtTime(0.5, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5)

      osc.start(now)
      osc.stop(now + 0.5)
    } else if (type === 'snare') {
      const noiseBuffer = ctx.createBuffer(
        1,
        ctx.sampleRate * 0.2,
        ctx.sampleRate,
      )
      const output = noiseBuffer.getChannelData(0)
      for (let i = 0; i < noiseBuffer.length; i++) {
        output[i] = Math.random() * 2 - 1
      }
      const noise = ctx.createBufferSource()
      noise.buffer = noiseBuffer
      const noiseFilter = ctx.createBiquadFilter()
      noiseFilter.type = 'highpass'
      noiseFilter.frequency.value = 1000
      const noiseGain = ctx.createGain()

      noise.connect(noiseFilter)
      noiseFilter.connect(noiseGain)
      noiseGain.connect(ctx.destination)

      noiseGain.gain.setValueAtTime(0.3, now)
      noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2)

      noise.start(now)
    } else if (type === 'woodblock') {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.frequency.setValueAtTime(800, now)
      osc.type = 'sine'

      gain.gain.setValueAtTime(0.3, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1)

      osc.start(now)
      osc.stop(now + 0.1)
    }
  } catch (e) {
    console.error('Drum playback failed', e)
  }
}

export const playClick = () => {
  playDrum('woodblock')
}
